'use client';

import { ChevronDown } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { type ReactNode, useState } from 'react';
import { Link, usePathname } from '@/lib/routing';
import { Icon } from '@/lib/ui-kit/icons';
import { Popover, PopoverContent, PopoverTrigger } from '@/lib/ui-kit/popover';
import { getZoneFromHref, getZoneFromPath } from '@/lib/utils/zones';
import { BalancePopoverContent } from './BalancePopoverContent';

interface BalancePopoverProps {
  onDeposit?: () => void;
  onConvert?: () => void;
  /** Currency of the popover's trading balance (trade zone passes the pair's settle coin). */
  balanceCurrency?: string;
}

export function BalancePopover({
  onDeposit,
  onConvert,
  balanceCurrency = 'USDC',
}: BalancePopoverProps) {
  const t = useTranslations('nav');
  const locale = useLocale();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  // Soft Link only inside the current zone (account pages are web, deals may be trade).
  const currentZone = getZoneFromPath(pathname);
  const renderAccountLink = (href: string, label: ReactNode): ReactNode => {
    if (getZoneFromHref(href) === currentZone) {
      return (
        <Link href={href} onClick={() => setOpen(false)}>
          {label}
        </Link>
      );
    }
    const prefix = locale === 'en' ? '' : `/${locale}`;
    return <a href={`${prefix}${href}`}>{label}</a>;
  };

  const handleDeposit = () => {
    setOpen(false);
    onDeposit?.();
  };
  const handleConvert = () => {
    setOpen(false);
    onConvert?.();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* Figma 14928:31340: a wallet icon and a chevron, no amount. */}
      <PopoverTrigger
        aria-label={t('wallet')}
        className="group hidden cursor-pointer items-center text-v2-text-primary outline-none transition-opacity hover:opacity-80 focus-visible:opacity-80 min-[1024px]:flex"
      >
        <span className="-mr-1 flex size-10 items-center justify-center">
          <Icon name="HeaderWallet" size={24} />
        </span>
        <ChevronDown
          aria-hidden
          className="size-4 text-v2-text-secondary transition-transform duration-200 group-data-[state=open]:rotate-180"
        />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="ph-no-capture w-auto border-0 bg-transparent p-0 shadow-none"
      >
        <BalancePopoverContent
          onDeposit={handleDeposit}
          onConvert={handleConvert}
          renderAccountLink={renderAccountLink}
          balanceCurrency={balanceCurrency}
        />
      </PopoverContent>
    </Popover>
  );
}
